import gql from "graphql-tag";
import className from "classnames/bind";
import { useRef } from 'react';
import { TextImageFragment } from "@graphqlTypes";
import { Button, Container, HeadlineAnim, RevealWrapper, Image } from "@components";

import { useViewSize } from '@/providers/ViewSizeContext';
import { useInView } from 'motion/react';
import { AnimVals } from '@/utils/animUtils';
import { useCalculatedScrollTrigger } from "@/hooks/calculatedScrollTrigger";

import styles from "./TextImage.module.scss";
import { getHighlightMarkup, getLinkData } from "@/core/utils";
const cx = className.bind(styles);

const TextImageItem = ({
  eyebrow,
  headline,
  body,
  cta,
  secondaryCta,
  image,
}: TextImageFragment["items"][number]) => {
  const ctaData = getLinkData(cta);
  const secondaryCtaData = getLinkData(secondaryCta);
  const showButtons = Boolean(ctaData || secondaryCtaData);
  return (
    <div className={cx("text-image-item")}>
      <Image image={image} className={cx("image")} />
      <div className={cx("content")}>
        { eyebrow && <div className={cx("eyebrow-l", "eyebrow")}>{eyebrow}</div> } 
        { headline && <h2 className={cx("headline", "title-lc-m")}>{headline}</h2> }
        <div
          className={cx("body", "rich-text", "text-m")}
          dangerouslySetInnerHTML={{ __html: body }}
        />
        { showButtons && (
          <div className={cx("buttons")}>
            {ctaData && <Button className={cx("cta")} {...ctaData} />}
            {secondaryCtaData && <Button className={cx("secondary-cta")} {...secondaryCtaData} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default function TextImage({ items, headline, subhead }: TextImageFragment) {
  const headlineMarkup = headline ? getHighlightMarkup(headline) : null;
  const showHeader = Boolean(headlineMarkup || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLElement | null>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const { triggerAmount } = useCalculatedScrollTrigger(rootEl, triggerPercent);

  const isInView = useInView(rootEl, { amount: triggerAmount, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(["text-image"])} ref={rootEl}>
      <RevealWrapper isInView={isInView}>
        {showHeader && (
          <Container className={cx("header")}>
            <HeadlineAnim
              tag="h1"
              text={headlineMarkup}
              className={cx(["title-uc-l"])}
              isRevealEnabled={isRevealEnabled}
              isInView={isInView}
            />
            <p className={cx("subhead", "text-m")}>{subhead}</p>
          </Container>
        )}
        <Container className={cx("container")}>
          {items.map((item, index) => (
            <TextImageItem key={index} {...item} />
          ))}
        </Container>
      </RevealWrapper>
    </section>
  );

}

TextImage.fragments = {
  entry: gql`
    fragment TextImage on FlexiblePageContentContentTextImageLayout {
      headline
      subhead
      items {
        eyebrow
        headline
        body
        cta {
          linkType
          externalLink
          label
          page {
            node {
              uri
            }
          }
        }
        secondaryCta {
          linkType
          externalLink
          label
          page {
            node {
              uri
            }
          }
        }
        image {
          ...ACFMedia
        }
      }
    }
  `,
};
